import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Activity,
  AlertTriangle,
  BellRing,
  BrainCircuit,
  CheckCircle2,
  Loader2,
  MessageCircle,
  PackageSearch,
  RefreshCw,
  X,
} from 'lucide-react';
import DeliveryActionModal from './DeliveryActionModal';
import DeliveryIntelligenceCard from './DeliveryIntelligenceCard';
import { apiErrorMessage, fetchRecipientIntelligence } from './operationalIntelligenceApi';

const TABS = [
  ['summary', 'Resumen', Activity],
  ['deliveries', 'Entregas', PackageSearch],
  ['communication', 'Comunicación', MessageCircle],
  ['intelligence', 'Contexto inteligente', BrainCircuit],
];

const STORE_SIGNAL_LABELS = {
  recipient_confirmation: 'Cliente confirmó a la tienda',
  recipient_response: 'Cliente respondió a la tienda',
  cancellation_request: 'Solicitud de cancelación',
  postponement_request: 'Solicitud de postergación',
};

const RISK_STYLES = {
  HIGH: 'bg-red-100 text-red-800 border-red-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW: 'bg-blue-100 text-blue-800 border-blue-200',
  NORMAL: 'bg-emerald-100 text-emerald-800 border-emerald-200',
};
const RISK_LABELS = { HIGH: 'ALTO', MEDIUM: 'MEDIO', LOW: 'BAJO', NORMAL: 'NORMAL' };

const READINESS_STYLES = {
  HIGH_ATTENTION: 'border-red-200 bg-red-100 text-red-800',
  REVIEW_RECOMMENDED: 'border-orange-200 bg-orange-100 text-orange-800',
  ELEVATED: 'border-amber-200 bg-amber-100 text-amber-800',
  WAITING_RESPONSE: 'border-blue-200 bg-blue-100 text-blue-800',
  NORMAL: 'border-emerald-200 bg-emerald-100 text-emerald-800',
};
const READINESS_LABELS = {
  HIGH_ATTENTION: 'ALTA ATENCIÓN',
  REVIEW_RECOMMENDED: 'REVISIÓN RECOMENDADA',
  ELEVATED: 'ELEVADO',
  WAITING_RESPONSE: 'ESPERANDO RESPUESTA',
  NORMAL: 'NORMAL',
};

function formatDate(value, withTime = false) {
  if (!value) return 'Sin registro';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('es-PE', {
    timeZone: 'America/Lima',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...(withTime ? { hour: '2-digit', minute: '2-digit' } : {}),
  }).format(date);
}

function messageVisual(message) {
  if (message.channel === 'whatsapp' && message.direction === 'inbound') {
    return { label: 'WHATSAPP · CLIENTE', dot: 'bg-emerald-500', box: 'border-emerald-200 bg-emerald-50' };
  }
  if (message.channel === 'whatsapp') {
    return { label: 'WHATSAPP · FAST MAX', dot: 'bg-green-600', box: 'border-green-200 bg-green-50/60' };
  }
  if (message.sender_type === 'system' || message.channel === 'system' || message.message_type === 'system') {
    return { label: 'SISTEMA', dot: 'bg-slate-500', box: 'border-slate-200 bg-slate-50' };
  }
  return {
    label: `INTERNO · ${(message.sender_type || 'OPERACIONES').toUpperCase()}`,
    dot: 'bg-brand-primary',
    box: 'border-pink-200 bg-pink-50/50',
  };
}

export default function RecipientIntelligenceDrawer({
  phone,
  preDispatchContext,
  storeSignalContext,
  onClose,
  onOverviewRefresh,
  onToast,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('summary');
  const [actionTarget, setActionTarget] = useState(null);

  const loadRecipient = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await fetchRecipientIntelligence(phone));
    } catch (requestError) {
      setError(apiErrorMessage(requestError, 'No se pudo cargar el destinatario'));
    } finally {
      setLoading(false);
    }
  }, [phone]);

  useEffect(() => {
    loadRecipient();
  }, [loadRecipient]);

  useEffect(() => {
    function handleKeyDown(event) {
      if (event.key === 'Escape' && !actionTarget) onClose();
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [actionTarget, onClose]);

  const recipient = data?.recipient;
  const communication = data?.communication_summary || {};
  const latestIntelligence = data?.conversation_intelligence?.[0];
  const activeDeliveries = useMemo(
    () => (data?.deliveries || []).filter((delivery) => delivery.is_active),
    [data]
  );

  async function handleActionCompleted(result) {
    setActionTarget(null);
    await Promise.all([loadRecipient(), onOverviewRefresh?.()]);
    onToast?.(
      result.action === 'postpone'
        ? `Entrega #${result.delivery?.id} postergada correctamente.`
        : `Entrega #${result.delivery?.id} cancelada correctamente.`
    );
  }

  return (
    <div className="fixed inset-0 z-[9000] flex justify-end bg-slate-950/45 backdrop-blur-[2px]" role="dialog" aria-modal="true">
      <button type="button" onClick={onClose} className="hidden flex-1 cursor-default sm:block" aria-label="Cerrar panel" />
      <div className="flex h-full w-full flex-col bg-slate-50 shadow-2xl sm:max-w-4xl">
        <header className="shrink-0 bg-gradient-to-r from-brand-primary to-pink-600 px-5 py-4 text-white sm:px-7">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-xs font-extrabold uppercase tracking-[0.22em] text-white/70">Destinatario 360°</p>
              <h2 className="mt-1 truncate font-montserrat text-2xl font-extrabold">
                {recipient?.recipient_name || 'Cargando destinatario...'}
              </h2>
              <p className="mt-1 font-mono text-sm text-white/85">{recipient?.phone || phone}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={loadRecipient} className="rounded-xl bg-white/10 p-2.5 hover:bg-white/20" aria-label="Actualizar destinatario">
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
              <button onClick={onClose} className="rounded-xl bg-white/10 p-2.5 hover:bg-white/20" aria-label="Cerrar">
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {recipient && (
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <span className={`rounded-full border px-3 py-1.5 text-sm font-extrabold ${RISK_STYLES[recipient.risk_level] || RISK_STYLES.NORMAL}`}>
                Riesgo histórico: {RISK_LABELS[recipient.risk_level] || recipient.risk_level} · {recipient.risk_score}
              </span>
              <span className="text-sm text-white/85">
                {recipient.terminal_attempts} intentos · {recipient.delivered} entregadas · {recipient.rejected} rechazadas · {recipient.cancelled} canceladas
              </span>
            </div>
          )}
        </header>

        <nav className="shrink-0 overflow-x-auto border-b border-slate-200 bg-white px-3 sm:px-6">
          <div className="flex min-w-max">
            {TABS.map(([value, label, Icon]) => (
              <button
                key={value}
                type="button"
                onClick={() => setActiveTab(value)}
                className={`flex items-center gap-2 border-b-2 px-4 py-3.5 text-sm font-bold transition-colors ${
                  activeTab === value
                    ? 'border-brand-primary text-brand-primary'
                    : 'border-transparent text-slate-500 hover:text-slate-800'
                }`}
              >
                <Icon className="h-4 w-4" />
                {label}
              </button>
            ))}
          </div>
        </nav>

        <main className="flex-1 overflow-y-auto p-4 sm:p-6">
          {loading && !data ? (
            <div className="flex h-72 items-center justify-center gap-3 text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin text-brand-primary" />
              Cargando contexto operativo...
            </div>
          ) : error ? (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-red-800">
              <p className="font-bold">No se pudo abrir el contexto</p>
              <p className="mt-1 text-sm">{error}</p>
              <button onClick={loadRecipient} className="mt-4 rounded-lg bg-red-700 px-4 py-2 text-sm font-bold text-white">Reintentar</button>
            </div>
          ) : (
            <>
              {activeTab === 'summary' && (
                <SummaryTab
                  recipient={recipient}
                  communication={communication}
                  activeDeliveries={activeDeliveries}
                  alerts={data?.alerts || []}
                  latestIntelligence={latestIntelligence}
                  preDispatchContext={preDispatchContext}
                  storeSignalContext={storeSignalContext}
                  onOpenDeliveries={() => setActiveTab('deliveries')}
                />
              )}
              {activeTab === 'deliveries' && (
                <div className="space-y-4">
                  <div className="flex flex-wrap items-end justify-between gap-3">
                    <div>
                      <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-400">Historial individual</p>
                      <h3 className="text-xl font-extrabold text-slate-900">Entregas más recientes</h3>
                    </div>
                    <p className="text-sm text-slate-500">{data?.delivery_count_returned || 0} de hasta {data?.delivery_limit || 100}</p>
                  </div>
                  {(data?.deliveries || []).map((delivery) => (
                    <DeliveryIntelligenceCard
                      key={delivery.id}
                      delivery={delivery}
                      onAction={(target, action) => setActionTarget({
                        delivery: target,
                        action,
                        sourceStoreSignal: (
                          Number(target.id) === Number(storeSignalContext?.delivery_point_id)
                          && (
                            (action === 'cancel' && storeSignalContext?.signal_type === 'cancellation_request')
                            || (action === 'postpone' && storeSignalContext?.signal_type === 'postponement_request')
                          )
                        ) ? storeSignalContext : null,
                      })}
                    />
                  ))}
                </div>
              )}
              {activeTab === 'communication' && (
                <CommunicationTab summary={communication} timeline={data?.communication_timeline || []} />
              )}
              {activeTab === 'intelligence' && (
                <IntelligenceTab
                  recipient={recipient}
                  communication={communication}
                  intelligence={data?.conversation_intelligence || []}
                  alerts={data?.alerts || []}
                />
              )}
            </>
          )}
        </main>
      </div>

      {actionTarget && (
        <DeliveryActionModal
          delivery={actionTarget.delivery}
          action={actionTarget.action}
          sourceStoreSignal={actionTarget.sourceStoreSignal}
          onClose={() => setActionTarget(null)}
          onCompleted={handleActionCompleted}
          onStateChanged={loadRecipient}
        />
      )}
    </div>
  );
}

function SummaryTab({
  recipient,
  communication,
  activeDeliveries,
  alerts,
  latestIntelligence,
  preDispatchContext,
  storeSignalContext,
  onOpenDeliveries,
}) {
  return (
    <div className="space-y-5">
      {preDispatchContext && <PreDispatchSnapshot item={preDispatchContext} />}
      {storeSignalContext && (
        <section className="rounded-2xl border border-violet-200 bg-violet-50 p-4 text-violet-950">
          <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.14em] text-violet-700"><BellRing className="h-4 w-4" /> Señal de la tienda</p>
          <p className="mt-2 text-sm font-extrabold">{STORE_SIGNAL_LABELS[storeSignalContext.signal_type] || storeSignalContext.signal_type} · {storeSignalContext.state}</p>
          {storeSignalContext.note && <p className="mt-2 text-xs leading-5 text-violet-800">“{storeSignalContext.note}”</p>}
          <p className="mt-2 text-xs text-violet-700">La señal es contexto auditado; el estado de la entrega solo cambia con una acción canónica.</p>
        </section>
      )}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label="Intentos terminales" value={recipient?.terminal_attempts || 0} tone="slate" />
        <Metric label="Entregadas" value={recipient?.delivered || 0} tone="emerald" />
        <Metric label="Fallidas" value={(recipient?.rejected || 0) + (recipient?.cancelled || 0)} tone="red" />
        <Metric label="Tiendas diferentes" value={recipient?.store_count || 0} tone="pink" />
      </div>

      {activeDeliveries.length > 0 && (
        <button
          type="button"
          onClick={onOpenDeliveries}
          className="flex w-full items-center justify-between rounded-2xl border border-brand-primary/20 bg-brand-primary/5 p-4 text-left hover:bg-brand-primary/10"
        >
          <div>
            <p className="font-extrabold text-brand-primary">{activeDeliveries.length} entrega(s) activa(s) requieren seguimiento</p>
            <p className="mt-0.5 text-sm text-slate-600">Revisa cada DeliveryPoint antes de tomar una acción.</p>
          </div>
          <PackageSearch className="h-6 w-6 shrink-0 text-brand-primary" />
        </button>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-sm font-extrabold uppercase tracking-[0.16em] text-slate-500">Contexto operativo</h3>
        <div className="mt-4 grid gap-5 md:grid-cols-2">
          <ContextBlock title="Historial">
            <p>{recipient?.rejected || 0} rechazos</p>
            <p>{recipient?.cancelled || 0} cancelaciones</p>
            <p>{recipient?.delivered || 0} entregas</p>
          </ContextBlock>
          <ContextBlock title="Comunicación">
            <p>{communication.outbound_count || 0} mensajes salientes registrados</p>
            <p>{communication.inbound_count || 0} respuestas entrantes registradas</p>
            <p>Última respuesta: {formatDate(communication.last_inbound_at, true)}</p>
          </ContextBlock>
          <ContextBlock title="Señales">
            <p>{communication.customer_not_answering_signals || 0} señales de cliente no contesta</p>
            <p>{communication.customer_requested_postponement_signals || 0} señales de postergación solicitada</p>
            <p>{alerts.length} alertas operativas vinculadas</p>
          </ContextBlock>
          <ContextBlock title="Contexto más reciente">
            <p>{latestIntelligence?.situation_summary || 'No hay contexto de conversación disponible para las entregas analizadas.'}</p>
          </ContextBlock>
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-400">Siguiente paso sugerido</p>
        <p className="mt-2 text-base font-semibold text-slate-800">
          {latestIntelligence?.recommended_next_step || 'Revisar la entrega activa y confirmar el contexto con operaciones antes de intervenir.'}
        </p>
        <p className="mt-2 text-xs text-slate-500">Sugerencia contextual. La decisión final permanece bajo control del administrador.</p>
      </section>
    </div>
  );
}

function PreDispatchSnapshot({ item }) {
  const readiness = item.readiness || {};
  const communication = item.communication || {};
  const account = item.account_history || {};
  const recipient = item.recipient_history || {};
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-brand-primary">Pre-despacho · entrega #{item.delivery_point_id}</p>
          <h3 className="mt-1 text-lg font-extrabold text-slate-900">Readiness actual de esta entrega</h3>
          <p className="mt-1 text-xs text-slate-500">Separado del riesgo histórico general del destinatario.</p>
        </div>
        <span className={`rounded-full border px-3 py-1.5 text-xs font-extrabold ${READINESS_STYLES[readiness.level] || READINESS_STYLES.NORMAL}`}>
          {READINESS_LABELS[readiness.level] || readiness.level}
        </span>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Comunicación</p>
          <p className="mt-1 font-bold text-slate-800">{(communication.state || 'sin datos').replaceAll('_', ' ')}</p>
          <p className="mt-1 text-xs text-slate-500">{communication.minutes_since_outbound == null ? 'Sin contacto saliente' : `${communication.minutes_since_outbound} min desde contacto`}</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Destinatario previo</p>
          <p className="mt-1 font-bold text-slate-800">{recipient.prior_cancelled ?? recipient.cancelled ?? 0} canceladas</p>
          <p className="mt-1 text-xs text-slate-500">{recipient.prior_rejected ?? recipient.rejected ?? 0} rechazadas · solo contexto</p>
        </div>
        <div className="rounded-xl bg-slate-50 p-3">
          <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-400">Cuenta · 90 días</p>
          <p className="mt-1 font-bold text-slate-800">{Number(account.cancellation_rate_pct || 0).toFixed(1)}% cancelación</p>
          <p className="mt-1 text-xs text-slate-500">{account.terminal_attempts || 0} intentos · {account.confidence || 'LOW'}</p>
        </div>
      </div>
      <div className="mt-4 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">
        <p className="font-extrabold">{readiness.recommendation}</p>
        <p className="mt-1 text-xs text-amber-800">Recomendación explicable; no ejecuta cancelación, postergación ni contacto automático.</p>
      </div>
    </section>
  );
}

function CommunicationTab({ summary, timeline }) {
  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Metric label="Conversaciones" value={summary.conversation_count || 0} tone="pink" />
        <Metric label="Internos" value={summary.internal_message_count || 0} tone="slate" />
        <Metric label="WhatsApp" value={summary.whatsapp_message_count || 0} tone="emerald" />
        <Metric label="Entrantes" value={summary.inbound_count || 0} tone="blue" />
      </div>

      {!summary.has_ever_replied && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
          No se encontraron respuestas entrantes registradas en los mensajes analizados.
        </div>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <div className="mb-5">
          <p className="text-xs font-extrabold uppercase tracking-[0.16em] text-slate-400">Línea de tiempo</p>
          <h3 className="text-xl font-extrabold text-slate-900">WhatsApp + Fast Max interno</h3>
        </div>
        {timeline.length ? (
          <div className="relative space-y-3 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-slate-200">
            {timeline.map((message) => {
              const visual = messageVisual(message);
              return (
                <div key={`${message.source}-${message.id}`} className="relative flex gap-4 pl-0">
                  <div className={`relative z-10 mt-4 h-3.5 w-3.5 shrink-0 rounded-full border-2 border-white shadow ${visual.dot}`} />
                  <div className={`min-w-0 flex-1 rounded-xl border p-3 ${visual.box}`}>
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <p className="text-[11px] font-extrabold uppercase tracking-wide text-slate-600">{visual.label}</p>
                      <p className="text-[11px] text-slate-500">{formatDate(message.created_at, true)}</p>
                    </div>
                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">
                      {message.content || `[${message.message_type || 'mensaje sin texto'}]`}
                    </p>
                    {message.delivery_point_id && <p className="mt-1 text-[11px] text-slate-400">Entrega #{message.delivery_point_id}</p>}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No hay mensajes vinculados a las entregas analizadas.</p>
        )}
      </section>
    </div>
  );
}

function IntelligenceTab({ recipient, communication, intelligence, alerts }) {
  return (
    <div className="space-y-5">
      <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
        <strong>Riesgo histórico y comunicación se mantienen separados.</strong> El puntaje de {recipient?.risk_score || 0} usa únicamente resultados de entrega, frecuencia, recencia y tamaño de muestra. Mensajes, alertas y señales solo explican el contexto.
      </div>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-extrabold text-slate-900">Evidencia del riesgo histórico</h3>
        <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {Object.entries(recipient?.evidence || {}).map(([key, value]) => (
            <div key={key} className="rounded-xl bg-slate-50 p-3">
              <p className="text-[11px] font-bold uppercase tracking-wide text-slate-400">{key.replaceAll('_', ' ')}</p>
              <p className="mt-1 text-lg font-extrabold text-slate-800">{value}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h3 className="text-lg font-extrabold text-slate-900">Inteligencia de conversaciones existente</h3>
        <p className="mt-1 text-xs text-slate-500">Lectura rule-based/persistida. Abrir este panel no genera borradores ni llamadas automáticas a OpenAI.</p>
        <div className="mt-4 space-y-3">
          {intelligence.length ? intelligence.map((item) => (
            <div key={item.conversation_id} className="rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-extrabold text-slate-800">Conversación #{item.conversation_id}</p>
                <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold uppercase text-slate-600">
                  {item.risk_level || 'sin riesgo'} · {item.source || 'rule_based'}
                </span>
              </div>
              <p className="mt-3 text-sm text-slate-800">{item.situation_summary || 'Sin resumen disponible.'}</p>
              {item.missing_info?.length > 0 && (
                <p className="mt-2 text-sm text-amber-800"><strong>Información faltante:</strong> {item.missing_info.join(', ')}</p>
              )}
              {item.recommended_next_step && (
                <p className="mt-2 text-sm text-brand-primary"><strong>Siguiente paso:</strong> {item.recommended_next_step}</p>
              )}
              {(item.triage?.triage_reasons || []).length > 0 && (
                <ul className="mt-3 space-y-1 text-xs text-slate-600">
                  {item.triage.triage_reasons.map((reason) => <li key={reason}>• {reason}</li>)}
                </ul>
              )}
            </div>
          )) : (
            <p className="rounded-xl bg-slate-50 p-4 text-sm text-slate-500">No hay conversaciones recientes con inteligencia disponible.</p>
          )}
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-3">
        <Signal icon={MessageCircle} label="Cliente no contesta" value={communication.customer_not_answering_signals || 0} />
        <Signal icon={RefreshCw} label="Postergaciones solicitadas" value={communication.customer_requested_postponement_signals || 0} />
        <Signal icon={AlertTriangle} label="Alertas vinculadas" value={alerts.length} />
      </section>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const tones = {
    slate: 'bg-slate-100 text-slate-800',
    emerald: 'bg-emerald-100 text-emerald-800',
    red: 'bg-red-100 text-red-800',
    pink: 'bg-pink-100 text-brand-primary',
    blue: 'bg-blue-100 text-blue-800',
  };
  return (
    <div className={`rounded-2xl p-4 ${tones[tone] || tones.slate}`}>
      <p className="text-xs font-bold uppercase tracking-wide opacity-70">{label}</p>
      <p className="mt-1 text-2xl font-extrabold">{value}</p>
    </div>
  );
}

function ContextBlock({ title, children }) {
  return (
    <div>
      <div className="flex items-center gap-2">
        <CheckCircle2 className="h-4 w-4 text-brand-primary" />
        <h4 className="font-extrabold text-slate-800">{title}</h4>
      </div>
      <div className="mt-2 space-y-1 border-l-2 border-slate-100 pl-4 text-sm text-slate-600">{children}</div>
    </div>
  );
}

function Signal({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="rounded-xl bg-brand-primary/10 p-2.5 text-brand-primary"><Icon className="h-5 w-5" /></div>
      <div>
        <p className="text-2xl font-extrabold text-slate-900">{value}</p>
        <p className="text-xs font-bold text-slate-500">{label}</p>
      </div>
    </div>
  );
}
